import { createContext, ReactNode, useContext, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap/dist/gsap';
import ScrollSmoother from 'gsap/dist/ScrollSmoother';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';

import { useCondensedMenu } from 'common/condensedMenu/CondensedMenuProvider';

const SMOOTH_SCROLL_CONFIG = { effects: true, smooth: 0.8 };
const CONDENSED_MENU_SCROLL_CONFIG = { effects: false, smooth: 0.1 };
const getScrollSmootherConfig = (isSmoothScrollEnabled: boolean, showMenu: boolean) => {
  if (isSmoothScrollEnabled) return SMOOTH_SCROLL_CONFIG;
  return showMenu ? CONDENSED_MENU_SCROLL_CONFIG : {};
};

export type ScrollerContextValue = {
  smoothScroller?: ScrollSmoother;
  isSmoothScrollEnabled?: boolean;
};

export const ScrollerContext = createContext<ScrollerContextValue>({});
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger, ScrollSmoother, useGSAP);
}

export const ScrollerProvider = ({
  children,
  isSmoothScrollEnabled,
}: {
  children: ReactNode;
  isSmoothScrollEnabled: boolean;
}) => {
  const [smoothScroller, setSmoothScroller] = useState<ScrollSmoother | undefined>(undefined);
  const { showMenu } = useCondensedMenu();

  useGSAP(
    () => {
      if (isSmoothScrollEnabled || showMenu) {
        const config = getScrollSmootherConfig(isSmoothScrollEnabled, showMenu);
        const smoothScroller = ScrollSmoother.create(config);

        setSmoothScroller(smoothScroller);
      }
    },
    {
      dependencies: [isSmoothScrollEnabled, showMenu],
      revertOnUpdate: true,
    }
  );
  return (
    <ScrollerContext.Provider value={{ smoothScroller, isSmoothScrollEnabled }}>
      <div {...(isSmoothScrollEnabled || showMenu ? { id: 'smooth-wrapper' } : {})}>
        <div {...(isSmoothScrollEnabled || showMenu ? { id: 'smooth-content' } : {})}>
          {children}
        </div>
      </div>
    </ScrollerContext.Provider>
  );
};

export const useScrollContext = (): ScrollerContextValue => {
  return useContext(ScrollerContext);
};
